import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React from 'react';

import type { LensAnchorProps } from '../../types';
import { getColorValue, getFocusRing } from '../../utilities';

const variants = {
  neutral: {
    color: getColorValue('inherit'),
    focusRing: getFocusRing(),
    underline: 'inactive',
  },
  primary: {
    color: getColorValue('primary'),
    focusRing: getFocusRing(),
    underline: 'inactive',
  },
  subtle: {
    color: getColorValue('body'),
    focusRing: getFocusRing(),
    underline: 'hover',
  },
};

const statusStyles = {
  enabled: css`
    cursor: pointer;
  `,
  disabled: css`
    pointer-events: none;
    color: ${getColorValue('disabledContent')};
  `,
};

const buttonStyles = {
  isButton: css`
    background: none;
    border: none;
    font: inherit;
  `,
};

const LinkWrapper = styled.a<LinkWrapperProps>`
  ${props => !props.disabled && `color: ${variants[props.variant].color}`};
  ${props => (props.disabled ? statusStyles.disabled : statusStyles.enabled)};
  ${props => props.as === 'button' && buttonStyles.isButton};
  ${props =>
    `text-decoration: ${
      variants[props.variant].underline === 'inactive' ? 'underline' : 'none'
    }`};
  border-radius: 0.28em;
  box-shadow: 0 0 0 1px transparent;
  text-underline-offset: 0.35em;
  transition: 0.3s box-shadow;
  ${props =>
    props.noWrap &&
    css`
      white-space: nowrap;
    `}
  &:hover {
    ${props =>
      `text-decoration: ${
        variants[props.variant].underline === 'hover' ? 'underline' : 'none'
      }`};
  }
  &:focus,
  &:focus-visible {
    outline: 1px solid transparent;
  }
  &:focus-visible {
    ${getFocusRing()};
  }
  &::-moz-focus-inner {
    border: 0;
  }
`;

// This implementation can trigger false positives for accessibility rules
// TODO: Improve button link semantics to avoid jsx-a11y/anchor-is-valid warnings
const Link = ({
  children,
  href,
  variant = 'primary',
  htmlTag = 'a',
  isDisabled,
  noWrap,
  ...props
}: LinkProps &
  React.ComponentProps<typeof LinkWrapper> &
  Omit<
    LensAnchorProps,
    keyof LinkProps & React.ComponentProps<typeof LinkWrapper>
  >) => (
  <LinkWrapper
    href={href}
    variant={variant}
    as={htmlTag}
    disabled={isDisabled}
    noWrap={noWrap}
    {...props}
  >
    {children}
  </LinkWrapper>
);

export const availableVariants = Object.keys(variants);

type LinkProps = {
  children?: React.ReactNode;
  href?: string;
  variant?: 'neutral' | 'primary' | 'subtle';
  htmlTag?: 'button' | 'a';
  isDisabled?: boolean;
  noWrap?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  target?: string;
};

type LinkWrapperProps = {
  children?: React.ReactNode;
  href?: string;
  disabled?: boolean;
  noWrap?: boolean;
  variant?: 'neutral' | 'primary' | 'subtle';
  as?: string;
  target?: string;
};

export default Link;
